import { API_URL } from '../services/apiConstants';

/**
 * Redux reducer for the OATH hearing representation selection flow.
 *
 * Manages state for:
 * - Fetching items from queued emails (the outreach email's hearings)
 * - Item selection (checkboxes)
 * - Submitting representation requests
 * - Success/error states
 *
 * API calls are handled within thunk actions, same shape as
 * resolveViolationsReducer's bulk-resolve flow.
 */

// Action Types
const FETCH_ITEMS_REQUEST = 'REPRESENT_OATH_HEARINGS/FETCH_ITEMS_REQUEST';
const FETCH_ITEMS_SUCCESS = 'REPRESENT_OATH_HEARINGS/FETCH_ITEMS_SUCCESS';
const FETCH_ITEMS_FAILURE = 'REPRESENT_OATH_HEARINGS/FETCH_ITEMS_FAILURE';

const SUBMIT_REPRESENTATION_REQUEST =
	'REPRESENT_OATH_HEARINGS/SUBMIT_REPRESENTATION_REQUEST';
const SUBMIT_REPRESENTATION_SUCCESS =
	'REPRESENT_OATH_HEARINGS/SUBMIT_REPRESENTATION_SUCCESS';
const SUBMIT_REPRESENTATION_FAILURE =
	'REPRESENT_OATH_HEARINGS/SUBMIT_REPRESENTATION_FAILURE';

const TOGGLE_ITEM_SELECTION = 'REPRESENT_OATH_HEARINGS/TOGGLE_ITEM_SELECTION';
const TOGGLE_ADDRESS_ITEMS = 'REPRESENT_OATH_HEARINGS/TOGGLE_ADDRESS_ITEMS';
const RESET_STATE = 'REPRESENT_OATH_HEARINGS/RESET_STATE';

// Initial State
const initialState = {
	items: [],
	selectedItems: [],
	isLoading: false,
	isSubmitting: false,
	error: null,
	submitSuccess: false,
	submitMessage: null,
};

/**
 * Represent OATH hearings reducer.
 *
 * @param {Object} state Current state
 * @param {Object} action Dispatched action
 * @returns {Object} New state
 */
export default function representOathHearingsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_ITEMS_REQUEST:
			return {
				...state,
				isLoading: true,
				error: null,
			};

		case FETCH_ITEMS_SUCCESS:
			return {
				...state,
				isLoading: false,
				items: action.payload.items,
				error: null,
			};

		case FETCH_ITEMS_FAILURE:
			return {
				...state,
				isLoading: false,
				error: action.payload.error,
			};

		case TOGGLE_ITEM_SELECTION: {
			const itemId = action.payload.queuedEmailId;
			const isSelected = state.selectedItems.includes(itemId);

			return {
				...state,
				selectedItems: isSelected
					? state.selectedItems.filter((id) => id !== itemId)
					: [...state.selectedItems, itemId],
			};
		}

		case TOGGLE_ADDRESS_ITEMS: {
			const { queuedEmailIds } = action.payload;

			const allSelected = queuedEmailIds.every((id) =>
				state.selectedItems.includes(id),
			);

			return {
				...state,
				selectedItems: allSelected
					? state.selectedItems.filter((id) => !queuedEmailIds.includes(id))
					: [...new Set([...state.selectedItems, ...queuedEmailIds])],
			};
		}

		case SUBMIT_REPRESENTATION_REQUEST:
			return {
				...state,
				isSubmitting: true,
				error: null,
				submitSuccess: false,
			};

		case SUBMIT_REPRESENTATION_SUCCESS:
			return {
				...state,
				isSubmitting: false,
				submitSuccess: true,
				submitMessage: action.payload.message,
				selectedItems: [],
			};

		case SUBMIT_REPRESENTATION_FAILURE:
			return {
				...state,
				isSubmitting: false,
				error: action.payload.error,
				submitSuccess: false,
			};

		case RESET_STATE:
			return initialState;

		default:
			return state;
	}
}

// Action Creators

/**
 * Request to fetch items for representation.
 * @returns {Object} Action
 */
export const fetchItemsRequest = () => ({
	type: FETCH_ITEMS_REQUEST,
});

/**
 * Successfully fetched items.
 * @param {Array} items Array of items
 * @returns {Object} Action
 */
export const fetchItemsSuccess = (items) => ({
	type: FETCH_ITEMS_SUCCESS,
	payload: { items },
});

/**
 * Failed to fetch items.
 * @param {string} error Error message
 * @returns {Object} Action
 */
export const fetchItemsFailure = (error) => ({
	type: FETCH_ITEMS_FAILURE,
	payload: { error },
});

/**
 * Toggle selection of a single item.
 * @param {number} queuedEmailId The queued email ID
 * @returns {Object} Action
 */
export const toggleItemSelection = (queuedEmailId) => ({
	type: TOGGLE_ITEM_SELECTION,
	payload: { queuedEmailId },
});

/**
 * Toggle selection of every open item under one address.
 * @param {Array<number>} queuedEmailIds Queued email IDs for that address's open items
 * @returns {Object} Action
 */
export const toggleAddressItems = (queuedEmailIds) => ({
	type: TOGGLE_ADDRESS_ITEMS,
	payload: { queuedEmailIds },
});

/**
 * Request to submit representation.
 * @returns {Object} Action
 */
export const submitRepresentationRequest = () => ({
	type: SUBMIT_REPRESENTATION_REQUEST,
});

/**
 * Successfully submitted representation request.
 * @param {string} message Success message
 * @returns {Object} Action
 */
export const submitRepresentationSuccess = (message) => ({
	type: SUBMIT_REPRESENTATION_SUCCESS,
	payload: { message },
});

/**
 * Failed to submit representation request.
 * @param {string} error Error message
 * @returns {Object} Action
 */
export const submitRepresentationFailure = (error) => ({
	type: SUBMIT_REPRESENTATION_FAILURE,
	payload: { error },
});

/**
 * Reset reducer state to initial.
 * @returns {Object} Action
 */
export const resetState = () => ({
	type: RESET_STATE,
});

// Thunk Actions

// Shown when the API refuses the link's signature (edited, or older than the link lifetime)
const INVALID_LINK_MESSAGE =
	'This link has expired or is invalid. Please use the Represent for Me button in your latest OATH hearings email.';

/**
 * API URL for both selection calls. The signature covers the link's exact query string, so it
 * is passed through untouched rather than rebuilt from parsed parameters.
 *
 * @param {string} signedQuery The selection page's own query string, including the leading "?"
 * @returns {string} Signed API URL
 */
const selectionUrl = (signedQuery) =>
	`${API_URL}/api/oath-represent-selection/items${signedQuery}`;

/**
 * Fetch items for the hearing-selection page.
 *
 * @param {string} signedQuery The page's signed query string (user_id, email_ids, expires, signature)
 * @returns {Function} Thunk function
 */
export const fetchItems = (signedQuery) => {
	return async (dispatch) => {
		dispatch(fetchItemsRequest());

		try {
			const response = await fetch(selectionUrl(signedQuery), {
				method: 'GET',
				headers: {
					Accept: 'application/json',
				},
			});

			if (response.status === 403) {
				throw new Error(INVALID_LINK_MESSAGE);
			}

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Failed to fetch items');
			}

			if (data.success && data.data) {
				dispatch(fetchItemsSuccess(data.data.items || []));
			} else {
				dispatch(fetchItemsFailure(data.message || 'Failed to fetch items'));
			}
		} catch (error) {
			console.error('fetchItems error:', error);
			dispatch(fetchItemsFailure(error.message || 'Network error occurred'));
		}
	};
};

/**
 * Submit the selected hearings for representation.
 *
 * @param {string} signedQuery The page's signed query string (user_id, email_ids, expires, signature)
 * @param {Array<number>} queuedEmailIds Selected queued_email ids, a subset of the link's email_ids
 * @returns {Function} Thunk function
 */
export const submitRepresentation = (signedQuery, queuedEmailIds) => {
	return async (dispatch) => {
		dispatch(submitRepresentationRequest());

		try {
			const response = await fetch(selectionUrl(signedQuery), {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					queued_email_ids: queuedEmailIds,
				}),
			});

			if (response.status === 403) {
				throw new Error(INVALID_LINK_MESSAGE);
			}

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Failed to submit representation request');
			}

			if (data.success) {
				dispatch(
					submitRepresentationSuccess(
						data.message || 'Representation request submitted successfully',
					),
				);
			} else {
				dispatch(
					submitRepresentationFailure(
						data.message || 'Failed to submit representation request',
					),
				);
			}
		} catch (error) {
			console.error('submitRepresentation error:', error);
			dispatch(
				submitRepresentationFailure(error.message || 'Network error occurred'),
			);
		}
	};
};
